import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Badge,
  DatePreset,
  datePresetOptions,
  FilterBar,
  FilterControlType,
  Page,
  SearchInput,
  SearchSpinner,
  SearchTable,
  SortMenu,
  TagBadges,
  type DateFilterDefinition,
  type FilterBarProps,
  type MultiselectFilterDefinition,
} from 'corso-admin-ui';
import {
  ClaimReasonEnum,
  ClaimResolutionMethodEnum,
  ClaimStatusEnum,
  CrewMerchantUi,
  shippingClaimReasonName,
  shippingClaimResolutionMethodName,
  shippingClaimStatusName,
  type CommandAdminApi,
} from 'corso-types';
import { useReducer } from 'react';
import { match } from 'ts-pattern';
import api from '~/api';
import { sharedTableDefinitions } from '~/components/dataTable/SharedTableDefinitions';
import env from '~/env';

type Request = CommandAdminApi.RequestBody<'/shipping-claim', 'post'>;
type ShippingClaim = CommandAdminApi.ResponseBody<
  '/shipping-claim',
  'post'
>['data'][number];

// declared rather than inferred so each filter's `value` carries the request's enum type
type Filters = {
  createdOn: DateFilterDefinition & { id: 'createdOn' };
  statuses: MultiselectFilterDefinition & {
    id: 'statuses';
    value?: Request['statuses'];
  };
  claimReasons: MultiselectFilterDefinition & {
    id: 'claimReasons';
    value?: Request['claimReasons'];
  };
  resolutionMethods: MultiselectFilterDefinition & {
    id: 'resolutionMethods';
    value?: Request['resolutionMethods'];
  };
};

const filters: Filters = {
  createdOn: {
    id: 'createdOn',
    label: 'Date',
    verb: 'is',
    type: FilterControlType.date,
    options: datePresetOptions,
  },
  statuses: {
    id: 'statuses',
    label: 'Status',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(ClaimStatusEnum).map((status) => ({
      value: status,
      label: shippingClaimStatusName[status],
    })),
  },
  claimReasons: {
    id: 'claimReasons',
    label: 'Reason',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(ClaimReasonEnum).map((reason) => ({
      value: reason,
      label: shippingClaimReasonName[reason],
    })),
  },
  resolutionMethods: {
    id: 'resolutionMethods',
    label: 'Resolution',
    verb: 'is',
    type: FilterControlType.multiselect,
    options: Object.values(ClaimResolutionMethodEnum).map((value) => ({
      value,
      label: shippingClaimResolutionMethodName[value],
    })),
  },
};

type State = {
  activeFilters: FilterBarProps<Filters>['activeFilters'];
  searchTerm: string;
  orderBy: Request['orderBy'];
  cursor: Request['cursor'];
};

type Action =
  | { type: 'filterChange'; filter: Filters[keyof Filters] }
  | { type: 'removeFilters'; ids: (keyof Filters)[] }
  | { type: 'clearFilters' }
  | { type: 'search'; searchTerm: string }
  | { type: 'sortChange'; orderBy: Request['orderBy'] }
  | { type: 'setCursor'; cursor: Request['cursor'] };

const initialState = {
  activeFilters: {
    createdOn: { kind: 'preset', preset: DatePreset.last30Days },
  },
  searchTerm: '',
  orderBy: 'desc',
  cursor: null,
} satisfies State;

// every change but paging resets the cursor; it points into the old result set
const reducer = (state: State, action: Action): State =>
  match(action)
    .with({ type: 'filterChange' }, ({ filter }) => ({
      ...state,
      activeFilters: { ...state.activeFilters, [filter.id]: filter.value },
      cursor: null,
    }))
    .with({ type: 'removeFilters' }, ({ ids }) => ({
      ...state,
      activeFilters: Object.fromEntries(
        Object.entries(state.activeFilters).filter(
          ([id]) => !ids.some((removed) => removed === id),
        ),
      ),
      cursor: null,
    }))
    .with({ type: 'clearFilters' }, () => initialState)
    .with({ type: 'search' }, ({ searchTerm }) => ({
      ...state,
      searchTerm,
      cursor: null,
    }))
    .with({ type: 'sortChange' }, ({ orderBy }) => ({
      ...state,
      orderBy,
      cursor: null,
    }))
    .with({ type: 'setCursor' }, ({ cursor }) => ({ ...state, cursor }))
    .exhaustive();

const resolutionBadgeVariant = {
  [ClaimResolutionMethodEnum.refund]: 'danger',
  [ClaimResolutionMethodEnum.reorder]: 'success',
} as const satisfies Record<ClaimResolutionMethodEnum, string>;

const columnHelper = createColumnHelper<ShippingClaim>();

const columns = [
  columnHelper.accessor('id', {
    header: 'Claim',
    cell: ({ getValue }) => (
      <span className="font-semibold">#{getValue()}</span>
    ),
  }),
  columnHelper.accessor('storeName', { header: 'Store' }),
  sharedTableDefinitions.createdOnColumn<ShippingClaim>({ header: 'Date' }),
  columnHelper.accessor('customerName', { header: 'Customer' }),
  columnHelper.accessor('customerEmail', { header: 'Email' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => shippingClaimStatusName[getValue()],
  }),
  columnHelper.accessor('resolutionMethod', {
    header: 'Resolution Method',
    cell: ({ getValue }) => (
      <Badge variant={resolutionBadgeVariant[getValue()]}>
        {shippingClaimResolutionMethodName[getValue()]}
      </Badge>
    ),
  }),
  columnHelper.accessor('reason', {
    header: 'Reason',
    cell: ({ getValue }) => shippingClaimReasonName[getValue()],
  }),
  columnHelper.accessor('tags', {
    header: 'Tags',
    cell: ({ getValue }) => <TagBadges tags={getValue()} />,
  }),
];

export function AllShippingClaimsPage() {
  const [state, dispatch] = useReducer(reducer, initialState);

  // the pinned date pill can't be removed, only emptied
  const createdOn =
    state.activeFilters.createdOn ?? initialState.activeFilters.createdOn;
  const searchTerm = state.searchTerm.trim();
  const request = {
    ...state.activeFilters,
    createdOn,
    ...(searchTerm && { searchTerm }),
    cursor: state.cursor,
    orderBy: state.orderBy,
  } satisfies Request;

  const isSearchTooLong =
    searchTerm.length > CrewMerchantUi.MAX_SEARCH_TERM_LENGTH;

  const query = useQuery({
    queryKey: ['shippingClaims', request],
    // the API rejects a custom range without a start
    enabled:
      !isSearchTooLong &&
      (createdOn.kind === 'preset' || createdOn.range.start !== null),
    queryFn: () => api.shippingClaims.list(request),
    placeholderData: keepPreviousData,
  });

  return (
    <Page title="Shipping Claims" size="fullWidth">
      <SearchTable
        controls={
          <div className="w-full divide-y divide-neutral-200 bg-white">
            <div className="flex items-center gap-2 p-2">
              <SearchInput
                defaultValue={state.searchTerm}
                placeholder="Search all"
                onSearch={(term) =>
                  dispatch({ type: 'search', searchTerm: term })
                }
              />
              <SearchSpinner show={query.isFetching} />
              <SortMenu
                options={[{ value: 'createdOn', label: 'Date' }]}
                value={{ column: 'createdOn', direction: state.orderBy }}
                onChange={({ direction }) =>
                  dispatch({ type: 'sortChange', orderBy: direction })
                }
              />
            </div>
            <FilterBar
              className="p-2"
              filters={filters}
              activeFilters={state.activeFilters}
              pinned={['createdOn']}
              onChange={(filter) => dispatch({ type: 'filterChange', filter })}
              onRemoveFilters={(ids) =>
                dispatch({ type: 'removeFilters', ids })
              }
            />
          </div>
        }
        data={query.data?.data ?? []}
        columns={columns}
        pageInfo={
          query.data?.pageInfo ?? { prevCursor: null, nextCursor: null }
        }
        onPageChange={(cursor) => dispatch({ type: 'setCursor', cursor })}
        onRowClick={(claim) =>
          window.open(
            `${env.VITE_MERCHANT_APP_URL}/${claim.storeId}/claims/shipping/${claim.id}`,
            '_blank',
            'noopener',
          )
        }
        isFetching={query.isFetching}
        isError={query.isError}
        isSearchTooLong={isSearchTooLong}
        onRetry={() => {
          // oxlint-disable-next-line @typescript-eslint/no-floating-promises
          query.refetch();
        }}
        entityLabel="shipping claims"
        onClearFilters={() => dispatch({ type: 'clearFilters' })}
      />
    </Page>
  );
}
